import axios from "axios";
import authService from "./authService";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://delivery-bonfimgourmet-244425130782.southamerica-east1.run.app";

const API_BASE = `${BACKEND_URL}/coupons`;

/**
 * Coupon Service
 * Gerencia todas as operações de cupons
 */
class CouponService {
  /**
   * Get authorization headers with JWT token
   */
  getAuthHeaders() {
    const token = authService.getToken();
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
  }

  /**
   * Get all coupons
   */
  async getAllCoupons() {
    try {
      const response = await axios.get(API_BASE, this.getAuthHeaders());
      return response.data?.data || response.data || [];
    } catch (error) {
      if (error.response?.status === 404) {
        return [];
      }
      console.error("❌ Erro ao buscar cupons:", error);
      throw error;
    }
  }

  /**
   * Get coupon by ID
   */
  async getCouponById(id) {
    try {
      const response = await axios.get(
        `${API_BASE}/${id}`,
        this.getAuthHeaders()
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error("❌ Erro ao buscar cupom:", error);
      throw error;
    }
  }

  /**
   * Validate coupon by code
   */
  async validateCoupon(code) {
    try {
      const response = await axios.get(
        `${API_BASE}/${code}/validate`,
        this.getAuthHeaders()
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error("❌ Erro ao validar cupom:", error);
      throw error;
    }
  }

  /**
   * Create new coupon
   */
  async createCoupon(couponData) {
    try {
      const response = await axios.post(
        API_BASE,
        couponData,
        this.getAuthHeaders()
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error("❌ Erro ao criar cupom:", error);
      throw error;
    }
  }

  /**
   * Update coupon
   */
  async updateCoupon(id, couponData) {
    try {
      const response = await axios.put(
        `${API_BASE}/${id}`,
        couponData,
        this.getAuthHeaders()
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error("❌ Erro ao atualizar cupom:", error);
      throw error;
    }
  }

  /**
   * Delete coupon
   */
  async deleteCoupon(id) {
    try {
      const response = await axios.delete(
        `${API_BASE}/${id}`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error("❌ Erro ao deletar cupom:", error);
      throw error;
    }
  }

  /**
   * Activate coupon
   */
  async activateCoupon(id) {
    try {
      const response = await axios.put(
        `${API_BASE}/${id}/activate`,
        {},
        this.getAuthHeaders()
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error("❌ Erro ao ativar cupom:", error);
      throw error;
    }
  }

  /**
   * Deactivate coupon
   */
  async deactivateCoupon(id) {
    try {
      const response = await axios.put(
        `${API_BASE}/${id}/deactivate`,
        {},
        this.getAuthHeaders()
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error("❌ Erro ao desativar cupom:", error);
      throw error;
    }
  }

  /**
   * Format date for display (DD/MM/YYYY)
   */
  formatDate(dateString) {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR");
  }

  /**
   * Format date for input (YYYY-MM-DD)
   */
  formatDateForInput(dateString) {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  }

  /**
   * Check if coupon is expired
   */
  isExpired(expirationDate) {
    if (!expirationDate) return false;
    return new Date(expirationDate) < new Date();
  }

  /**
   * Get status badge info
   */
  getStatusBadge(coupon) {
    if (!coupon.active) {
      return { variant: "destructive", label: "Inativo" };
    }
    if (this.isExpired(coupon.expiration_date)) {
      return { variant: "secondary", label: "Expirado" };
    }
    return { variant: "success", label: "Ativo" };
  }

  /**
   * Validate coupon data
   */
  validateCouponData(data) {
    const errors = [];

    if (!data.code || data.code.trim().length === 0) {
      errors.push("Código do cupom é obrigatório");
    }

    if (data.code && data.code.length > 20) {
      errors.push("Código deve ter no máximo 20 caracteres");
    }

    if (!data.discount_percentage || data.discount_percentage <= 0) {
      errors.push("Desconto deve ser maior que zero");
    }

    if (data.discount_percentage > 100) {
      errors.push("Desconto não pode ser maior que 100%");
    }

    if (!data.expiration_date) {
      errors.push("Data de expiração é obrigatória");
    }

    return errors;
  }
}

export default new CouponService();
